import { useCallback } from 'react';

import { defineMessage, useIntl } from 'react-intl';

import FeatureGates from '@atlaskit/feature-gate-js-client';
import { EMPTY_CONVERSATION_ID } from '@atlassian/conversation-assistant-store/controllers/chat';
import { useUIStore } from '@atlassian/conversation-assistant-store/controllers/ui-store';
import type { InsightReadyHostMeta } from '@atlassian/rovo-growth-pulse/ui/insights-feed/insight-detail/types';

import { useSetBackBehaviorOverride } from '../../common/ui/useSetBackBehaviorOverride';
import { useActiveAction } from '../active-action';

import { insightToConversationAction } from './insight-to-conversation-action';

const backToInsightsMessage = defineMessage({
	id: 'ai-mate.seed-insight-conversation.back-to-insights.label',
	defaultMessage: 'Go back to Insights',
	description:
		'Label for the back button shown in the chat header after opening a Rovo Insight in chat. When clicked, it returns the user to the Rovo Insights list.',
});

export type SeedInsightConversation = (
	agentMessage: string,
	meta: InsightReadyHostMeta,
) => Promise<void>;

/**
 * Returns a stable callback that opens an insight in chat via `activeAction`.
 * Must be used within the conversation assistant store and
 * {@link BackBehaviorOverrideProvider} trees.
 */
export const useSeedInsightConversation = (): SeedInsightConversation => {
	const { setActiveAction, clearActiveAction } = useActiveAction();
	const { setActiveMenu, setConversationHeader } = useUIStore();
	const setBackBehaviorOverride = useSetBackBehaviorOverride();
	const { formatMessage } = useIntl();

	return useCallback(
		async (agentMessage: string, meta: InsightReadyHostMeta): Promise<void> => {
			// Insight activeAction opens are pulse-only.
			if (!FeatureGates.getExperimentValue('rovo_growth_chat_pulse', 'isEnabled', false)) {
				return;
			}

			const action = insightToConversationAction(
				{
					agentMessageAdf: agentMessage,
					title: meta.conversationTitle,
					followUps: meta.followUps,
				},
				meta.conversationIcon,
				{
					onDismiss: () => {
						setConversationHeader(null);
						setBackBehaviorOverride(null);
					},
					onCreated: (conversationId) => {
						setConversationHeader({
							conversationId,
							title: meta.conversationTitle,
							icon: meta.conversationIcon,
							iconAppearance: meta.conversationIconAppearance,
							source: 'pulse',
						});
						setBackBehaviorOverride(null);
					},
				},
			);

			setActiveAction(action);

			setConversationHeader({
				conversationId: EMPTY_CONVERSATION_ID,
				title: meta.conversationTitle,
				icon: meta.conversationIcon,
				iconAppearance: meta.conversationIconAppearance,
				source: 'pulse',
			});
			setBackBehaviorOverride({
				source: 'pulse',
				backButtonLabel: meta.backButtonLabel ?? formatMessage(backToInsightsMessage),
				conversationId: EMPTY_CONVERSATION_ID,
				previousView: () => {
					setActiveMenu('pulse');
					clearActiveAction();
				},
			});

			setActiveMenu('none');
		},
		[
			setActiveAction,
			clearActiveAction,
			setConversationHeader,
			setBackBehaviorOverride,
			setActiveMenu,
			formatMessage,
		],
	);
};
